import { Injectable } from '@angular/core';
import { ERROR_CODES } from '../constants/fhir.constants';
import type { ConversionError } from '../interfaces/fhir-converter.interfaces';

/** Valid FHIR R4 AdministrativeGender values. */
const VALID_GENDERS = ['male', 'female', 'other', 'unknown'] as const;

/**
 * FHIR R4 Practitioner Validator
 *
 * Implements FHIR R4 Practitioner validation:
 *
 * - Gender value-set conformance (`male | female | other | unknown`)
 * - Qualification sub-element required fields: `code` (1..1)
 */
@Injectable({ providedIn: 'root' })
export class FhirPractitionerValidator {
  validate(resource: unknown, errors: ConversionError[] = [], warnings: string[] = []): boolean {
    const r = resource as Record<string, unknown>;

    // Gender value-set
    this.validateGender(r, errors);

    // Qualification sub-elements
    this.validateQualifications(r, errors);

    // Informational warnings
    if (!r['name'] || (Array.isArray(r['name']) && (r['name'] as unknown[]).length === 0)) {
      warnings.push('Practitioner has no name specified');
    }

    return !errors.some((e) => e.severity === 'error');
  }

  private validateGender(r: Record<string, unknown>, errors: ConversionError[]): void {
    const gender = r['gender'] as string | undefined;
    if (gender && !VALID_GENDERS.includes(gender as (typeof VALID_GENDERS)[number])) {
      errors.push({
        code: ERROR_CODES.PRAC_INVALID_GENDER,
        message: `Invalid gender "${gender}". Must be one of: ${VALID_GENDERS.join(', ')}`,
        path: 'gender',
        severity: 'error',
      });
    }
  }

  /** Qualification.code is required (1..1). */
  private validateQualifications(r: Record<string, unknown>, errors: ConversionError[]): void {
    const qualifications = r['qualification'] as Record<string, unknown>[] | undefined;
    if (!qualifications || !Array.isArray(qualifications)) return;

    for (let i = 0; i < qualifications.length; i++) {
      if (!qualifications[i]['code']) {
        errors.push({
          code: ERROR_CODES.PRAC_QUALIFICATION_MISSING_CODE,
          message: `qualification[${i}].code is required`,
          path: `qualification[${i}].code`,
          severity: 'error',
        });
      }
    }
  }
}
